import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Box, Typography
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import {
    IRecordPaymentRequest,
    SalaryPaymentMethod,
} from '../../interfaces/salaryPayment';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { helpWhy } from '../../help/why';

interface Props {
    open: boolean;
    onClose: () => void;
    payrollId: string;
    netSalary: number;
    outstanding: number;
    onSuccess: () => void;
}

const METHODS: SalaryPaymentMethod[] = ['CASH', 'BANK_TRANSFER', 'MOBILE_BANKING', 'CHEQUE', 'OTHER'];

// Positive decimal with up to 2 decimal places. Disallows leading/trailing spaces
// (caller trims before validating) and requires at least one digit.
const AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const todayIso = () => toLocalIsoDate();

const PaymentFormDialog: React.FC<Props> = ({ open, onClose, payrollId, netSalary, outstanding, onSuccess }) => {
    const { showSuccess, showApiError } = useToast();
    const [submitting, setSubmitting] = useState(false);

    const [amount, setAmount] = useState<string>('');
    const [amountError, setAmountError] = useState<string>('');
    const [paymentDate, setPaymentDate] = useState<string>(todayIso());
    const [paymentMethod, setPaymentMethod] = useState<SalaryPaymentMethod>('CASH');

    const [bankName, setBankName] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [transactionId, setTransactionId] = useState('');

    const [mobileProvider, setMobileProvider] = useState('');
    const [mobileAccount, setMobileAccount] = useState('');

    const [chequeNumber, setChequeNumber] = useState('');
    const [chequeBank, setChequeBank] = useState('');
    const [chequeDate, setChequeDate] = useState<string>('');

    const [notes, setNotes] = useState('');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setAmount(outstanding > 0 ? outstanding.toFixed(2) : '');
            setPaymentDate(todayIso());
            setPaymentMethod('CASH');
            setBankName('');
            setBankAccountNumber('');
            setTransactionId('');
            setMobileProvider('');
            setMobileAccount('');
            setChequeNumber('');
            setChequeBank('');
            setChequeDate('');
            setNotes('');
            setReason('');
            setAmountError('');
        }
    }, [open, outstanding]);

    // Mirrors SalaryPaymentService.validateMethodSpecificFields: these methods need their details.
    const detailsMissing = paymentMethod === 'BANK_TRANSFER' && (!bankName.trim() || !bankAccountNumber.trim() || !transactionId.trim())
        ? 'Enter the bank name, account number and transaction ID first.'
        : paymentMethod === 'MOBILE_BANKING' && (!mobileProvider.trim() || !mobileAccount.trim() || !transactionId.trim())
        ? 'Enter the mobile provider, mobile account and transaction ID first.'
        : paymentMethod === 'CHEQUE' && (!chequeNumber.trim() || !chequeBank.trim() || !chequeDate)
        ? 'Enter the cheque number, cheque bank and cheque date first.'
        : null;

    const handleSubmit = async () => {
        const trimmed = amount.trim();
        if (!AMOUNT_REGEX.test(trimmed) || trimmed === '0' || trimmed === '0.0' || trimmed === '0.00') {
            setAmountError('Enter a positive amount with up to 2 decimal places');
            return;
        }
        setAmountError('');

        const req: IRecordPaymentRequest = {
            amount: trimmed,
            paymentDate,
            paymentMethod,
            notes: notes || null,
            reason: reason || null,
        };

        if (paymentMethod === 'BANK_TRANSFER') {
            req.bankName = bankName || null;
            req.bankAccountNumber = bankAccountNumber || null;
            req.transactionId = transactionId || null;
        } else if (paymentMethod === 'MOBILE_BANKING') {
            req.mobileProvider = mobileProvider || null;
            req.mobileAccount = mobileAccount || null;
            req.transactionId = transactionId || null;
        } else if (paymentMethod === 'CHEQUE') {
            req.chequeNumber = chequeNumber || null;
            req.chequeBank = chequeBank || null;
            req.chequeDate = chequeDate || null;
        }

        setSubmitting(true);
        try {
            await PayrollService.recordPayment(payrollId, req);
            showSuccess('Payment recorded');
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, 'Failed to record payment');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.payForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogContent dividers data-help-section="payroll.payForm.section.details">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Net salary: {netSalary.toFixed(2)} &nbsp;|&nbsp; Outstanding: {outstanding.toFixed(2)}
                    </Typography>

                    <TextField
                        data-help="payroll.payForm.field.amount" label="Amount"
                        type="text"
                        value={amount}
                        onChange={(e) => {
                            setAmount(e.target.value);
                            if (amountError) setAmountError('');
                        }}
                        inputProps={{ inputMode: 'decimal' }}
                        required
                        fullWidth
                        error={!!amountError}
                        helperText={amountError || 'Decimal with up to 2 places (e.g. 1234.56)'}
                    />

                    <TextField
                        data-help="payroll.payForm.field.date" label="Payment Date"
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        fullWidth
                    />

                    <FormControl fullWidth required>
                        <InputLabel id="payment-method-label">Payment Method</InputLabel>
                        <Select
                            data-help="payroll.payForm.field.method" labelId="payment-method-label"
                            label="Payment Method"
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value as SalaryPaymentMethod)}
                            inputProps={{ 'aria-label': 'Payment Method' }}
                        >
                            {METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {paymentMethod === 'BANK_TRANSFER' && (
                        <>
                            <TextField
                                data-help="payroll.payForm.field.bankName" label="Bank Name" required
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                data-help="payroll.payForm.field.bankAccount" label="Bank Account Number" required
                                value={bankAccountNumber}
                                onChange={(e) => setBankAccountNumber(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                data-help="payroll.payForm.field.transactionId" label="Transaction ID" required
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}

                    {paymentMethod === 'MOBILE_BANKING' && (
                        <>
                            <TextField
                                data-help="payroll.payForm.field.mobileProvider" label="Mobile Provider" required
                                value={mobileProvider}
                                onChange={(e) => setMobileProvider(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                data-help="payroll.payForm.field.mobileAccount" label="Mobile Account" required
                                value={mobileAccount}
                                onChange={(e) => setMobileAccount(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                data-help="payroll.payForm.field.transactionId" label="Transaction ID" required
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}

                    {paymentMethod === 'CHEQUE' && (
                        <>
                            <TextField
                                data-help="payroll.payForm.field.chequeNumber" label="Cheque Number" required
                                value={chequeNumber}
                                onChange={(e) => setChequeNumber(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                data-help="payroll.payForm.field.chequeBank" label="Cheque Bank" required
                                value={chequeBank}
                                onChange={(e) => setChequeBank(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                data-help="payroll.payForm.field.chequeDate" label="Cheque Date" required
                                type="date"
                                value={chequeDate}
                                onChange={(e) => setChequeDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </>
                    )}

                    <TextField
                        data-help="payroll.payForm.field.notes" label="Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />

                    <TextField
                        data-help="payroll.payForm.field.reason" label="Reason (required if overpaying)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        helperText="Required when payment exceeds outstanding amount"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.payForm.cancel" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button data-help="payroll.payForm.save" {...helpWhy(submitting ? { kind: 'status', message: 'Saving is in progress.' } : detailsMissing ? { kind: 'status', message: detailsMissing } : null)} onClick={handleSubmit} variant="contained" disabled={submitting || !!detailsMissing}>
                    {submitting ? 'Saving...' : 'Record'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentFormDialog;
